// src/hooks/notifications/useNotifications.js
import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const apiUrl = process.env.REACT_APP_API_URL || "";

// Fetch notifications function
const fetchNotifications = async (getAccessTokenSilently, params = {}) => {
  const token = await getAccessTokenSilently();

  const { page = 1, limit = 20, type, read, sort = "createdAt:desc" } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sort,
  });

  if (type) queryParams.append("type", type);
  if (typeof read === "boolean") queryParams.append("read", read.toString());

  const url = `${apiUrl}/api/notifications?${queryParams.toString()}`;

  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

// Fetch unread count function
const fetchUnreadCount = async (getAccessTokenSilently) => {
  const token = await getAccessTokenSilently();

  const url = `${apiUrl}/api/notifications/unread-count`;

  const { data } = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

/**
 * Hook to fetch notifications with pagination and filters
 */
export const useNotifications = (params = {}) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    ["notifications", params],
    () => fetchNotifications(getAccessTokenSilently, params),
    {
      staleTime: 30000, // 30 seconds (reduced from 1 minute)
      cacheTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!getAccessTokenSilently, // Only run when authenticated
      retry: false, // Don't retry on failure
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: true, // Allow refetch on component remount
    }
  );
};

/**
 * Hook to fetch unread notification count
 */
export const useUnreadCount = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    "unreadCount",
    () => fetchUnreadCount(getAccessTokenSilently),
    {
      staleTime: 15000, // 15 seconds (reduced from 30)
      cacheTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!getAccessTokenSilently, // Only run when authenticated
      retry: false, // Don't retry on failure
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: true, // Allow refetch on component remount
      refetchInterval: false, // Disable automatic refetching for now
    }
  );
};
